import { IcoCheckCircle, IcoPlus, IcoX } from '@components/icons';
import { BtnPrimary, BtnSecondary, BtnPreWarning, BtnWarning } from '@components/buttons';
import { BtnCancel } from '@components/dialog';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useBodyScrollLock } from 'client/lib/hooks/use-body-scroll-lock';
import { ManagedTag } from './state';
import { errorMessage } from './tag-dialogs';

type Summary = ManagedTag & { fileCount: number };
type Edit = { kind: 'rename' | 'delete'; tag?: Summary; value: string; error: string };

export function TagDrawer({
  open,
  tags,
  summaries,
  summaryStatus,
  onRetrySummaries,
  onClose,
  onViewFiles,
  onCreate,
  onRename,
  onDelete,
}: {
  open: boolean;
  tags: ManagedTag[];
  summaries: Summary[];
  summaryStatus: 'idle' | 'loading' | 'ready' | 'error';
  onRetrySummaries(): void;
  onClose(): void;
  onViewFiles(tagId: string): void;
  onCreate(name: string): Promise<unknown>;
  onRename(tag: ManagedTag, name: string): Promise<unknown>;
  onDelete(tag: ManagedTag): Promise<unknown>;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const editInput = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<Edit>();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [createError, setCreateError] = useState('');
  useBodyScrollLock(open);
  useEffect(() => {
    const el = dialog.current;
    if (!el) {
      return;
    }
    if (open && !el.open) {
      el.showModal();
      searchInput.current?.focus();
    }
    if (!open && el.open) {
      el.close();
    }
    return () => {
      if (el.open) {
        el.close();
      }
    };
  }, [open]);
  useEffect(() => {
    if (edit?.kind !== 'delete') {
      editInput.current?.focus();
    }
  }, [edit?.kind, edit?.tag?.id]);
  const summaryById = new Map(summaries.map((item) => [item.id, item]));
  const query = search.trim();
  const visible = tags.filter((tag) => tag.name.toLowerCase().includes(query.toLowerCase()));
  const canCreate = !!query && !tags.some((tag) => tag.name.toLowerCase() === query.toLowerCase());
  function begin(kind: Edit['kind'], tag?: ManagedTag) {
    if (saving) {
      return;
    }
    setSuccess('');
    setCreateError('');
    const summary = tag && (summaryById.get(tag.id) ?? { ...tag, fileCount: 0 });
    setEdit({ kind, tag: summary, value: kind === 'rename' ? (tag?.name ?? '') : '', error: '' });
  }
  function validate(name: string, except?: string) {
    if (!name) {
      return 'Enter a tag name.';
    }
    if (tags.some((tag) => tag.id !== except && tag.name.toLowerCase() === name.toLowerCase())) {
      return 'A tag with this name already exists.';
    }
    return '';
  }
  async function create(event: Event) {
    event.preventDefault();
    if (!canCreate || saving) {
      return;
    }
    setSaving(true);
    setSuccess('');
    setCreateError('');
    setEdit(undefined);
    try {
      await onCreate(query);
      setSearch('');
      setSuccess(`Tag created: “${query}”`);
    } catch (err) {
      setCreateError(errorMessage(err));
    } finally {
      setSaving(false);
      searchInput.current?.focus();
    }
  }
  async function submit(event: Event) {
    event.preventDefault();
    if (!edit || saving) {
      return;
    }
    const name = edit.value.trim();
    const error = validate(name, edit.tag?.id);
    if (error) {
      setEdit({ ...edit, error });
      editInput.current?.focus();
      return;
    }
    setSaving(true);
    try {
      if (edit.kind === 'rename' && edit.tag) {
        await onRename(edit.tag, name);
        setSuccess(`Tag renamed: “${name}”`);
      }
      setEdit(undefined);
    } catch (err) {
      setEdit({ ...edit, error: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  }
  async function confirmDelete() {
    if (!edit?.tag || saving) {
      return;
    }
    setSaving(true);
    try {
      await onDelete(edit.tag);
      setSuccess(`Tag deleted: “${edit.tag.name}”`);
      setEdit(undefined);
    } catch (err) {
      setEdit({ ...edit, error: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  }
  function close() {
    if (saving) {
      return;
    }
    setEdit(undefined);
    setSearch('');
    setSuccess('');
    setCreateError('');
    onClose();
  }
  return (
    <dialog
      ref={dialog}
      aria-labelledby="tag-drawer-title"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClose={close}
      class="ml-auto mr-0 mt-0 mb-0 h-dvh max-h-dvh w-full max-w-[540px] border-0 bg-white p-0 shadow-2xl backdrop:bg-gray-950/40 sm:w-[540px]"
    >
      <div class="flex h-full flex-col">
        <header class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 id="tag-drawer-title" class="text-lg font-semibold text-gray-900">
            Manage tags
          </h2>
          <button
            type="button"
            disabled={saving}
            onClick={close}
            aria-label="Close tag management"
            class="flex size-11 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <IcoX class="size-5" />
          </button>
        </header>
        <div class="flex-1 overflow-y-auto p-5">
          <form onSubmit={create}>
            <input
              ref={searchInput}
              type="search"
              aria-label="Search or create tags"
              placeholder="Search or create tags"
              value={search}
              disabled={saving}
              onInput={(e) => {
                setSearch(e.currentTarget.value);
                setCreateError('');
              }}
              class="ruz-input w-full text-sm"
            />
            {canCreate && (
              <button
                type="submit"
                disabled={saving}
                class="mt-2 flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm font-semibold text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50"
              >
                <IcoPlus class="size-4 shrink-0" />
                <span class="min-w-0 truncate">Create tag “{query}”</span>
              </button>
            )}
            {createError && (
              <p role="alert" class="mt-2 text-sm text-red-700">
                {createError}
              </p>
            )}
          </form>
          <div role="status" aria-live="polite" aria-atomic="true">
            {success && (
              <div class="mt-4 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <IcoCheckCircle class="size-5 shrink-0" />
                <span class="min-w-0 break-words">{success}</span>
              </div>
            )}
          </div>
          <div class="mt-5 grid grid-cols-[minmax(0,1fr)_5.5rem] gap-3 border-b border-gray-200 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:grid-cols-[minmax(0,1fr)_5.5rem_15rem]">
            <span>Name</span>
            <span class="whitespace-nowrap text-right">Active files</span>
            <span class="hidden sm:block" aria-hidden="true" />
          </div>
          {summaryStatus === 'error' && (
            <p role="alert" class="mt-3 text-sm text-red-700">
              Active file counts could not load.{' '}
              <button type="button" class="font-semibold underline" onClick={onRetrySummaries}>
                Try again
              </button>
            </p>
          )}
          {visible.length === 0 && (
            <p class="py-6 text-sm text-gray-600">
              {tags.length === 0
                ? 'No tags yet. Type a name above to create one.'
                : 'No tags found.'}
            </p>
          )}
          {visible.map((tag) => (
            <div key={tag.id} class="border-b border-gray-200 py-2.5">
              <div class="grid grid-cols-[minmax(0,1fr)_5.5rem] items-center gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_5.5rem_15rem]">
                <strong class="truncate text-sm font-medium text-gray-900" title={tag.name}>
                  {tag.name}
                </strong>
                <span class="text-right text-sm tabular-nums text-gray-600">
                  {summaryStatus === 'ready' ? (summaryById.get(tag.id)?.fileCount ?? '—') : '—'}
                </span>
                <div class="col-span-2 flex flex-wrap justify-end gap-2 sm:col-span-1 sm:flex-nowrap">
                  <BtnSecondary
                    type="button"
                    disabled={saving}
                    class="min-h-11 rounded-full px-3 py-2 text-xs sm:min-h-0"
                    onClick={() => onViewFiles(tag.id)}
                  >
                    View files
                  </BtnSecondary>
                  <BtnSecondary
                    type="button"
                    disabled={saving}
                    class="min-h-11 rounded-full px-3 py-2 text-xs sm:min-h-0"
                    onClick={() => begin('rename', tag)}
                  >
                    Rename
                  </BtnSecondary>
                  <BtnPreWarning
                    type="button"
                    disabled={saving}
                    class="min-h-11 rounded-full px-3 py-2 text-xs sm:min-h-0"
                    onClick={() => begin('delete', tag)}
                  >
                    Delete
                  </BtnPreWarning>
                </div>
              </div>
              {edit?.tag?.id === tag.id && edit.kind === 'rename' && (
                <form onSubmit={submit} class="mt-3 rounded-lg bg-gray-50 p-3">
                  <label class="text-sm font-medium" for="tag-drawer-name">
                    Rename tag
                  </label>
                  <div class="mt-2">
                    <input
                      ref={editInput}
                      id="tag-drawer-name"
                      class="ruz-input"
                      maxLength={255}
                      disabled={saving}
                      value={edit.value}
                      onInput={(e) => setEdit({ ...edit, value: e.currentTarget.value, error: '' })}
                    />
                    <div class="mt-4 flex flex-wrap items-center gap-4">
                      <BtnPrimary disabled={saving} type="submit" class="rounded-full">
                        Save
                      </BtnPrimary>
                      <BtnCancel disabled={saving} type="button" onClick={() => setEdit(undefined)}>
                        Cancel
                      </BtnCancel>
                    </div>
                  </div>
                  {edit.error && (
                    <p role="alert" class="mt-2 text-sm text-red-700">
                      {edit.error}
                    </p>
                  )}
                </form>
              )}
              {edit?.tag?.id === tag.id && edit.kind === 'delete' && (
                <div class="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                  <strong class="text-sm">Delete “{tag.name}”?</strong>
                  <p class="mt-1 text-sm text-gray-700">
                    This tag will be removed from files, including archived files. The files stay in
                    the library.
                  </p>
                  <div class="mt-4 flex flex-wrap items-center gap-4">
                    <BtnWarning
                      type="button"
                      disabled={saving}
                      onClick={confirmDelete}
                      class="rounded-full"
                    >
                      Delete tag
                    </BtnWarning>
                    <BtnCancel type="button" disabled={saving} onClick={() => setEdit(undefined)}>
                      Cancel
                    </BtnCancel>
                  </div>
                  {edit.error && (
                    <p role="alert" class="mt-2 text-sm text-red-700">
                      {edit.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </dialog>
  );
}
